import apiService from './api';
import { trackMetaEvent } from '../utils/metaPixel';

/**
 * Shoppable DTC Lite service
 * Handles cart interactions and order tracking via the GoBag backend.
 *
 * Docs reference:
 *  - addToCart:  window.addToShoppableCart({ products: [{upc, name, quantity}], variation })
 *  - Cart icon:  <button id="shoppable-cart-button" onclick="window.toggleShoppableCart()">
 *                  <span id="shoppable-cart-quantity">0</span>
 *  - Events:     window.addEventListener("onCartLoaded", (e) => { e.detail.cartContent ... })
 *  - onOrderPlaced eventSpecificData = order number string (NOT an object)
 */

const SHOPPABLE_API = '/api/shoppable';

/** Build Meta Pixel `contents` payload from Shoppable cart state. */
function extractContents(cartContent) {
  const contents = [];
  (cartContent?.merchants || []).forEach((merchant) => {
    (merchant.products || []).forEach((product) => {
      contents.push({
        id: product.upc || product.name,
        quantity: product.quantity || 1,
        item_price: Number(product.salePrice || product.price || 0),
      });
    });
  });
  return contents;
}

/** Sum all merchant subtotals in the Shoppable cart. */
function cartTotal(cartContent) {
  return (cartContent?.merchants || []).reduce(
    (sum, merchant) => sum + (Number(merchant.subTotal) || 0),
    0
  );
}

class ShoppableService {
  /**
   * Add one or more products to the Shoppable cart.
   *
   * Per docs: window.addToShoppableCart({
   *   products: [{ upc, name, quantity }],
   *   variation: false
   * })
   *
   * @param {Array<{upc: string, name: string, quantity: number}>} products
   * @param {boolean} variation - show variation selector (false for bundles)
   */
  addToCart(products, variation = false) {
    if (!products || products.length === 0) {
      console.warn('[Shoppable] addToCart called with no products');
      return;
    }

    console.group('[Shoppable] 📦 addToCart');
    console.log('Products:', JSON.stringify(products, null, 2));
    console.log('Variation:', variation);

    const payload = { products, variation };
    console.log('Full payload:', JSON.stringify(payload, null, 2));

    if (typeof window.addToShoppableCart === 'function') {
      window.addToShoppableCart(payload);
      console.log('✅ window.addToShoppableCart called');
    } else {
      console.warn('[Shoppable] Cart not ready yet. Retrying in 1s...');
      setTimeout(() => {
        if (typeof window.addToShoppableCart === 'function') {
          window.addToShoppableCart(payload);
          console.log('✅ window.addToShoppableCart called (retry)');
        } else {
          console.error('[Shoppable] ❌ Cart still not ready after retry');
        }
      }, 1000);
    }
    console.groupEnd();
  }

  /**
   * Convenience: add a single item card's product to cart.
   * @param {{ upc: string, name: string, quantity?: number }} item
   */
  addItemToCart(item) {
    this.addToCart([{
      upc: item.upc,
      name: item.name || '',
      quantity: item.quantity || 1,
    }], false);
  }

  /**
   * Toggle the Shoppable cart panel open/closed.
   */
  toggleCart() {
    if (typeof window.toggleShoppableCart === 'function') {
      window.toggleShoppableCart();
    }
  }

  /**
   * Track a completed order in the GoBag backend.
   * @param {string} orderNumber  - string from onOrderPlaced eventSpecificData
   * @param {object} cartContent  - cartContent from event.detail.cartContent
   * @param {string|null} bagId
   */
  async trackOrder(orderNumber, cartContent, bagId = null) {
    try {
      await apiService.post(`${SHOPPABLE_API}/track-order`, {
        order_number: orderNumber,
        cart_content: cartContent,
        bag_id: bagId,
      });
    } catch (err) {
      console.error('[Shoppable] Failed to track order:', err);
    }
  }

  /**
   * Track a cart event in the GoBag backend.
   * @param {string} eventType        - DTC Lite event name (e.g. "onAddToCart")
   * @param {object} eventSpecificData - from event.detail.eventSpecificData
   */
  async trackEvent(eventType, eventSpecificData) {
    try {
      await apiService.post(`${SHOPPABLE_API}/events`, {
        event_type: eventType,
        event_data: eventSpecificData || {},
      });
    } catch (err) {
      console.error('[Shoppable] Failed to track event:', err);
    }
  }

  /**
   * Fetch the user's Shoppable orders from the backend.
   */
  async getOrders(limit = 50, offset = 0, status = null) {
    try {
      const params = new URLSearchParams({ limit, offset });
      if (status) params.set('status', status);
      const data = await apiService.get(`${SHOPPABLE_API}/orders?${params}`);
      return { success: true, data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }

  /**
   * Register all DTC Lite event listeners using the correct
   * window.addEventListener pattern (per Shoppable v6 docs).
   *
   * Events broadcast via window CustomEvent:
   *   event.detail.cartContent        — current cart state
   *   event.detail.eventSpecificData  — event-specific payload
   *
   * onOrderPlaced: eventSpecificData = order number STRING
   *
   * @param {{ isAuthenticated: boolean, bagId?: string, onOrderPlaced?: fn, onAddToCart?: fn }} options
   */
  registerEventListeners({ isAuthenticated = false, bagId = null, onOrderPlaced, onAddToCart } = {}) {
    if (typeof window === 'undefined') return;

    window.addEventListener('onCartLoaded', (e) => {
      const cart = e.detail?.cartContent;
      console.group('[Shoppable] 🛒 Cart Loaded');
      console.log('Cart content:', cart);
      if (cart?.merchants) {
        cart.merchants.forEach((m) => {
          console.log(`  ${m.name}:`, {
            subTotal: m.subTotal,
            isFreeShipping: m.isFreeShipping,
            freeShippingThreshold: m.freeShippingThreshold,
            productCount: m.products?.length,
            products: m.products?.map((p) => ({
              name: p.name,
              upc: p.upc,
              price: p.price,
              salePrice: p.salePrice,
              quantity: p.quantity,
              availability: p.availability,
            })),
          });
        });
      }
      console.groupEnd();
    });

    window.addEventListener('onAddToCart', (e) => {
      const { cartContent, eventSpecificData } = e.detail || {};
      console.group('[Shoppable] ➕ Add to Cart');
      console.log('Added products:', eventSpecificData);
      console.log('Updated cart content:', cartContent);
      if (cartContent?.merchants) {
        cartContent.merchants.forEach((m) => {
          console.log(`  ${m.name}: ${m.products?.length || 0} products, $${m.subTotal}`);
          (m.products || []).forEach((p) => {
            console.log(`    - ${p.name} | UPC: ${p.upc} | $${p.salePrice || p.price} | Qty: ${p.quantity} | Availability:`, p.availability);
          });
        });
      }
      console.groupEnd();
      if (onAddToCart) onAddToCart(eventSpecificData, cartContent);
      trackMetaEvent('AddToCart', {
        content_type: 'product',
        contents: extractContents(cartContent),
      });
    });

    window.addEventListener('onRemoveFromCart', (e) => {
      console.log('[Shoppable] Remove from cart', e.detail?.eventSpecificData);
    });

    window.addEventListener('onQuantityChanged', (e) => {
      console.log('[Shoppable] Quantity changed', e.detail?.eventSpecificData);
    });

    window.addEventListener('onMerchantChanged', (e) => {
      console.log('[Shoppable] Merchant changed', e.detail?.eventSpecificData);
    });

    window.addEventListener('onTracKLinkOff', (e) => {
      console.log('[Shoppable] Track link off', e.detail?.eventSpecificData);
    });

    window.addEventListener('onCheckoutStarted', (e) => {
      console.log('[Shoppable] Checkout started');
      if (isAuthenticated) {
        this.trackEvent('onCheckoutStarted', null);
      }
      trackMetaEvent('InitiateCheckout', {
        content_type: 'product',
        contents: extractContents(e.detail?.cartContent),
      });
    });

    window.addEventListener('onPaymentStarted', (e) => {
      console.log('[Shoppable] Payment started');
    });

    window.addEventListener('onOrderPlaced', (e) => {
      // Per docs: eventSpecificData = order number string
      const orderNumber = e.detail?.eventSpecificData;
      const cartContent = e.detail?.cartContent;
      console.log('[Shoppable] Order placed', orderNumber, cartContent);

      if (onOrderPlaced) onOrderPlaced(orderNumber, cartContent);

      if (orderNumber && cartContent) {
        this.trackOrder(orderNumber, cartContent, bagId);
      } else {
        console.warn('[Shoppable] Skipping order track — missing orderNumber or cartContent');
      }

      if (cartContent) {
        trackMetaEvent('Purchase', {
          value: cartTotal(cartContent),
          currency: 'USD',
          content_type: 'product',
          contents: extractContents(cartContent),
        });
      }
    });

    window.addEventListener('onPaymentFailed', (e) => {
      console.warn('[Shoppable] Payment failed', e.detail?.eventSpecificData);
      if (isAuthenticated) {
        this.trackEvent('onPaymentFailed', e.detail?.eventSpecificData || {});
      }
    });

    window.addEventListener('onCartFailed', (e) => {
      const reason = e.detail?.eventSpecificData;
      console.group('[Shoppable] ❌ Cart Failed');
      console.error('Reason:', reason);
      console.error('Full detail:', e.detail);
      console.groupEnd();
    });

    window.addEventListener('onCartOpened', (e) => {
      console.log('[Shoppable] Cart panel opened', e.detail?.eventSpecificData);
    });

    window.addEventListener('onCartClosed', () => {
      console.log('[Shoppable] Cart panel closed');
    });

    window.addEventListener('onCouponApplied', (e) => {
      console.log('[Shoppable] Coupon applied', e.detail?.eventSpecificData);
    });

    window.addEventListener('onCouponRemoved', (e) => {
      console.log('[Shoppable] Coupon removed', e.detail?.eventSpecificData);
    });

    window.addEventListener('onSubstitutionConsentToggled', (e) => {
      console.log('[Shoppable] Substitution consent toggled', e.detail?.eventSpecificData);
    });

    window.addEventListener('onMarketingConsentToggled', (e) => {
      console.log('[Shoppable] Marketing consent toggled', e.detail?.eventSpecificData);
    });

    window.addEventListener('onSecondaryMarketingConsentToggled', (e) => {
      console.log('[Shoppable] Secondary marketing consent toggled', e.detail?.eventSpecificData);
    });

    window.addEventListener('onExpressCheckoutStarted', (e) => {
      console.log('[Shoppable] Express checkout started', e.detail?.eventSpecificData);
    });

    window.addEventListener('onCartReset', () => {
      console.log('[Shoppable] Cart reset');
    });
  }
}

const shoppableService = new ShoppableService();
export default shoppableService;

